import { pendingUpgrade, clearPendingUpgrade } from "../billing/pendingUpgrade";
import OpenStratifyButton from '../components/OpenStratifyButton';
import { billingReturnTrace } from '../billing/appReturn';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Meta } from "../layout/Meta";
import Link from "../components/Link";
import BillingAuthPanel from "../components/BillingAuthPanel";
import { useBillingAuth } from "../billing/useBillingAuth";
import { fetchEntitlement } from "../billing/billingApiClient";
import { pollEntitlementUntilPaid } from "../billing/entitlementPoll";
import { billingWebConfig } from "../billing/billingConfig";
import { captureScope, isCurrentScope } from "../auth/authSession";
import { adaptEntitlement } from "../account/entitlementAdapter";

export default function CheckoutSuccessPage() {
  const { user, signedIn, ready, uid, generation } = useBillingAuth();
  const [phase, setPhase] = useState("idle");
  const [entitlement, setEntitlement] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [observedTransition, setObservedTransition] = useState(false);
  const sessionKey = `${uid || "none"}:${generation ?? "0"}`;
  const startedRef = useRef("");



  const runPoll = useCallback(
    async (signal) => {
      const clock = Date.now();
      billingReturnTrace("verification_start");
      setPhase("polling");
      const scope = captureScope();
      const pending = pendingUpgrade(scope);
      const result = await pollEntitlementUntilPaid({
        signal,
        targetPlan: pending?.targetPlan,
        isCurrentSession: () => isCurrentScope(scope),
        fetchEntitlement: async ({ signal: inner } = {}) => {
          const response = await fetchEntitlement({ scope, signal: inner || signal, reconcile:true, correlationId:pending?.correlationId });
          return { entitlement: response.entitlement };
        },
        onTick: ({ attempt, entitlement: next }) => {
          if (!isCurrentScope(scope)) return;
          billingReturnTrace("verification_tick", { attempt, plan: next?.plan, revision: next?.revision, targetPlan:pending?.targetPlan, providerCheckedAt:next?.providerCheckedAt, correlationId:pending?.correlationId, elapsedMs: Date.now() - clock });
          setAttempts(attempt);
          if (next) setEntitlement(next);
        },
      });

      if (!isCurrentScope(scope) || result.status === "aborted") return;
      billingReturnTrace("verification_result", { status: result.status, plan: result.entitlement?.plan, revision: result.entitlement?.revision, elapsedMs: Date.now() - clock });
      setEntitlement(result.entitlement);
      setAttempts(result.attempts);
      setObservedTransition(Boolean(result.observedTransition));
      if (result.status === "paid") {clearPendingUpgrade(scope); setPhase("confirmed");}
      else if (result.status === "signed_out") setPhase("signed_out");
      else setPhase("processing");
    },
    []
  );

  useEffect(() => {
    if (!ready) return undefined;
    if (!signedIn) {
      setPhase("signed_out");
      return undefined;
    }
    if (startedRef.current === sessionKey) return undefined;
    startedRef.current = sessionKey;
    const controller = new AbortController();
    runPoll(controller.signal);
    return () => controller.abort();
  }, [ready, signedIn, sessionKey, runPoll]);

  const adapted = adaptEntitlement(
    { status: entitlement ? "ready" : "unknown", entitlement },
    { ownerUid: uid }
  );
  const planLabel = adapted.planLabel;

  return (
    <>
      <Meta
        title="Checkout status"
        description="Confirming your Stratify subscription. This page does not grant access by itself."
        path="/checkout/success"
        noindex
      />
      <div className="mx-auto w-full max-w-3xl px-5 py-12 md:px-8">
        <h1 id="checkout-success-h1" className="text-3xl font-semibold tracking-tight text-ink md:text-4xl">
          Checkout status
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted">
          Stratify is checking your subscription. You can return to the app while confirmation finishes.
        </p>
        {billingWebConfig.sandboxLane ? (
          <p role="status" className="mt-3 text-sm leading-relaxed text-muted">
            Local staging sandbox. This page polls the staging Billing API. Do not treat this as production access.
          </p>
        ) : null}



        <OpenStratifyButton />

        <div className="mt-6">
          <BillingAuthPanel user={user} compact />
        </div>

        <div className="mt-6 rounded-2xl border border-ink/8 bg-white p-4" role="status">
          {phase === "polling" || phase === "idle" ? (
            <p className="text-sm text-muted">Confirming subscription… (attempt {attempts || 1})</p>
          ) : null}
          {phase === "confirmed" && observedTransition ? (
            <p className="text-sm text-ink/80">
              Plan confirmed{planLabel ? `: ${planLabel}` : ""}. Open the Stratify iOS app and sign in with this account
              to use Pro or Elite features.
            </p>
          ) : null}
          {phase === "confirmed" && !observedTransition ? (
            <p className="text-sm text-ink/80">
              Your current plan is {planLabel || "not a new upgrade"}. This page did not observe a billing change from
              this Checkout.
            </p>
          ) : null}
          {phase === "processing" ? (
            <div className="space-y-2 text-sm text-muted">
              <p>
                Still processing. Your payment may have succeeded, but billing confirmation has not arrived yet. This
                app will refresh your plan when billing confirms.
              </p>
              <p>Your account is showing {planLabel}. Sync subscription status to check Stripe again.</p>
            </div>
          ) : null}
          {phase === "signed_out" ? (
            <p className="text-sm text-muted">Sign in with the same account you used for Checkout to check status.</p>
          ) : null}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            onClick={() => {
              startedRef.current = "";
              if (signedIn) {
                const controller = new AbortController();
                startedRef.current = sessionKey;
                runPoll(controller.signal);
              }
            }}
            className="inline-flex min-h-11 items-center justify-center rounded-xl px-4 text-sm font-semibold text-ink/80 ring-1 ring-ink/10"
          >
            Sync subscription status
          </button>
          <Link
            to="/account/billing"
            className="inline-flex min-h-11 items-center justify-center rounded-xl px-4 text-sm font-semibold text-ink/80 ring-1 ring-ink/10"
          >
            Account billing
          </Link>
          <Link
            to="/pricing"
            className="inline-flex min-h-11 items-center justify-center rounded-xl px-4 text-sm font-semibold text-ink/80 ring-1 ring-ink/10"
          >
            Back to pricing
          </Link>
          <Link
            to={billingWebConfig.supportUrl}
            className="inline-flex min-h-11 items-center justify-center rounded-xl px-4 text-sm font-semibold text-ink/80 ring-1 ring-ink/10"
          >
            Contact support
          </Link>
        </div>
      </div>
    </>
  );
}
